import React from "react";
import { useNavigate } from "react-router-dom";
import { useContent, useEntries } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Overline } from "@/components/kit";
import { Subs } from "@/components/Subs";
import { exportYear } from "@/lib/pdf";
import { Lock, Download } from "lucide-react";

function Pair({ label, then, now }) {
  return (
    <Card className="space-y-3">
      <div className="font-serif text-xl text-gold">{label}</div>
      <div>
        <div className="overline mb-1">Then · ME₀</div>
        <p className="text-inksoft leading-relaxed whitespace-pre-wrap">{then || <span className="text-faint italic">Not recorded.</span>}</p>
      </div>
      <div className="border-t border-line pt-3">
        <div className="overline mb-1">Now · ME₁₁</div>
        <p className="text-inksoft leading-relaxed whitespace-pre-wrap">{now || <span className="text-faint italic">Not recorded.</span>}</p>
      </div>
    </Card>
  );
}

export default function Shift() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: content } = useContent();
  const { data: entries } = useEntries();
  if (!content || !entries) return <div className="text-inkfaint text-sm animate-pulse">Loading…</div>;

  const byMonth = Object.fromEntries(entries.map((e) => [e.month_id, e]));
  const m0 = byMonth[0]?.answers || {};
  const m11 = byMonth[11]?.answers || {};
  const unlocked = byMonth[11]?.completed;

  if (!unlocked) {
    return (
      <div className="max-w-xl mx-auto text-center em-fade-up" data-testid="shift-locked">
        <Overline>ME₀ : ME₁₁</Overline>
        <Lock className="w-8 h-8 text-faint mx-auto my-6" />
        <h1 className="font-serif text-4xl text-ink">Not yet</h1>
        <p className="text-inksoft mt-4 leading-relaxed">
          Complete ME₁₁: Emergence to place the person who began this journey beside the person who is
          here now. For eleven months you looked forward. This is where you finally look back.
        </p>
        <Btn className="mt-8" onClick={() => navigate("/journey")}>Continue your journey</Btn>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto em-fade-up" data-testid="shift-page">
      <Overline>ME₀ : ME₁₁</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-2">Then &amp; Now</h1>
      <p className="text-inksoft mt-4 mb-8 leading-relaxed">
        Not to decide whether one is better, but to see what changed. Read each dimension as it was, and
        as it is now.
      </p>

      <div className="space-y-4">
        {content.dims.map(([key, title]) => (
          <Pair key={key} label={title} then={m0[`m0_${key}`]} now={m11[`m11_${key}`]} />
        ))}
        <Pair label="What I wanted" then={m0["m0_want"]} now={m11["m11_d_want"]} />
        <Card>
          <div className="font-serif text-xl text-gold mb-2"><Subs>Could ME₀ have imagined me?</Subs></div>
          <p className="text-inksoft leading-relaxed whitespace-pre-wrap">
            {m11["m11_shift_imagine"] || <span className="text-faint italic">Not recorded.</span>}
          </p>
        </Card>
      </div>

      <Btn className="w-full mt-8" onClick={() => exportYear(content.states, byMonth, user)} data-testid="export-year-btn">
        <Download className="w-4 h-4" /> Export my year as PDF
      </Btn>
    </div>
  );
}
